/**
 * Agenda Annotations System
 *
 * Allows councillors and members of the public to annotate published agendas.
 * Uses PDF.js for rendering and annotation storage in WordPress user meta.
 *
 * @since 2.1.0
 */

/* global pdfjsLib, DemocraticAnnotations */
/* eslint-disable no-console, no-alert, no-unused-vars */

(function ($) {
	'use strict';

	const AgendaAnnotations = {
		pdfDoc: null,
		pageNum: 1,
		pageRendering: false,
		pageNumPending: null,
		scale: 1.5,
		canvas: null,
		ctx: null,
		annotations: [],
		currentAnnotation: null,
		annotationMode: 'none', // 'none', 'highlight', 'note', 'draw'
		isDrawing: false,
		drawingData: [],
		localStorageKey: null,

		/**
     * Initialize annotations system
     */
		init() {
			if (typeof pdfjsLib === 'undefined' || typeof DemocraticAnnotations === 'undefined') {
				console.error('PDF.js library not loaded');
				return;
			}

			this.canvas = document.getElementById('pdf-canvas');
			if (!this.canvas) return;
			this.localStorageKey = `agenda_annotations_${DemocraticAnnotations.agendaId || 'unknown'}`;

			this.ctx = this.canvas.getContext('2d');
			this.loadPdfDocument();
			this.loadAnnotations();
			this.bindEvents();
			this.updateAuthModeUI();
		},

		/**
     * Load PDF document
     */
		async loadPdfDocument() {
			const pdfUrl = $('#pdf-canvas').data('pdf-url');
			if (!pdfUrl) return;

			try {
				// Set worker path
				pdfjsLib.GlobalWorkerOptions.workerSrc =
          DemocraticAnnotations.pdfWorkerSrc;

				// Load PDF
				const loadingTask = pdfjsLib.getDocument(pdfUrl);
				this.pdfDoc = await loadingTask.promise;

				// Update page count
				$('#page-count').text(this.pdfDoc.numPages);

				// Render first page
				this.renderPage(this.pageNum);
			} catch (error) {
				console.error('Error loading PDF:', error);
				this.showError('Failed to load agenda document.');
			}
		},

		/**
     * Render PDF page with text layer for proper text selection
     * @param num
     */
		async renderPage(num) {
			this.pageRendering = true;

			try {
				const page = await this.pdfDoc.getPage(num);
				const viewport = page.getViewport({ scale: this.scale });

				// Set canvas dimensions
				this.canvas.height = viewport.height;
				this.canvas.width = viewport.width;

				// Render PDF page into canvas context
				const renderContext = {
					canvasContext: this.ctx,
					viewport,
				};

				await page.render(renderContext).promise;

				// Render text layer for text selection
				await this.renderTextLayer(page, viewport);

				// Render annotations for this page
				this.renderAnnotations(num);

				this.pageRendering = false;

				// If another page rendering is pending
				if (this.pageNumPending !== null) {
					this.renderPage(this.pageNumPending);
					this.pageNumPending = null;
				}

				// Update page number display
				$('#page-num').text(num);
			} catch (error) {
				console.error('Error rendering page:', error);
				this.pageRendering = false;
			}
		},

		/**
     * Render text layer for proper text selection
     * This fixes the "wrong text highlighted" bug by ensuring text layer
     * is properly aligned with the canvas rendering.
     *
     * @param {PDFPageProxy} page - The PDF.js page object
     * @param {PageViewport} viewport - The viewport object
     */
		async renderTextLayer(page, viewport) {
			// Get or create the text layer container
			let textLayerDiv = document.getElementById('pdf-text-layer');

			if (!textLayerDiv) {
				// Create text layer container if it doesn't exist
				textLayerDiv = document.createElement('div');
				textLayerDiv.id = 'pdf-text-layer';
				textLayerDiv.className = 'textLayer';

				// Insert after canvas in the same parent
				if (this.canvas.parentNode) {
					this.canvas.parentNode.appendChild(textLayerDiv);
				}
			}

			// Clear previous text layer content
			textLayerDiv.innerHTML = '';

			// Set text layer dimensions to match canvas
			textLayerDiv.style.width = `${viewport.width}px`;
			textLayerDiv.style.height = `${viewport.height}px`;

			// Get text content from the page
			const textContent = await page.getTextContent();

			// Render text items in the text layer
			// Each text item contains: str (text), transform (position matrix), width, height
			textContent.items.forEach((item) => {
				if (!item.str || item.str.trim() === '') {
					return;
				}

				// Create span for each text item
				const textSpan = document.createElement('span');
				textSpan.textContent = item.str;

				// Apply transform from PDF coordinate system
				// The transform matrix is [a, b, c, d, e, f] where:
				// a = horizontal scale, d = vertical scale
				// e = x position, f = y position
				const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);

				// Calculate position and size
				const fontSize = Math.sqrt(tx[0] * tx[0] + tx[1] * tx[1]);
				const angle = Math.atan2(tx[1], tx[0]);

				// Position the text span
				textSpan.style.position = 'absolute';
				textSpan.style.left = `${tx[4]}px`;
				// PDF coordinates are bottom-up, so we need to flip
				textSpan.style.top = `${tx[5] - fontSize}px`;
				textSpan.style.fontSize = `${fontSize}px`;
				textSpan.style.fontFamily = item.fontName || 'sans-serif';

				// Apply rotation if needed
				if (Math.abs(angle) > 0.01) {
					textSpan.style.transform = `rotate(${angle}rad)`;
					textSpan.style.transformOrigin = '0 0';
				}

				// Make text transparent but selectable
				textSpan.style.color = 'transparent';
				textSpan.style.whiteSpace = 'pre';

				textLayerDiv.appendChild(textSpan);
			});
		},

		/**
     * Queue page rendering
     * @param num
     */
		queueRenderPage(num) {
			if (this.pageRendering) {
				this.pageNumPending = num;
			} else {
				this.renderPage(num);
			}
		},

		/**
     * Go to previous page
     */
		onPrevPage() {
			if (this.pageNum <= 1) return;
			this.pageNum--;
			this.queueRenderPage(this.pageNum);
		},

		/**
     * Go to next page
     */
		onNextPage() {
			if (this.pageNum >= this.pdfDoc.numPages) return;
			this.pageNum++;
			this.queueRenderPage(this.pageNum);
		},

		/**
     * Zoom in
     */
		zoomIn() {
			this.scale = Math.min(this.scale + 0.25, 3.0);
			this.queueRenderPage(this.pageNum);
		},

		/**
     * Zoom out
     */
		zoomOut() {
			this.scale = Math.max(this.scale - 0.25, 0.5);
			this.queueRenderPage(this.pageNum);
		},

		/**
     * Bind event handlers
     */
		bindEvents() {
			// Navigation
			$('#prev-page').on('click', () => this.onPrevPage());
			$('#next-page').on('click', () => this.onNextPage());

			// Zoom
			$('#zoom-in').on('click', () => this.zoomIn());
			$('#zoom-out').on('click', () => this.zoomOut());

			// Annotation tools
			$('#tool-highlight').on('click', () =>
				this.setAnnotationMode('highlight'),
			);
			$('#tool-note').on('click', () => this.setAnnotationMode('note'));
			$('#tool-draw').on('click', () => this.setAnnotationMode('draw'));
			$('#tool-none').on('click', () => this.setAnnotationMode('none'));

			// Canvas interactions
			$(this.canvas).on('mousedown', (e) => this.onCanvasMouseDown(e));
			$(this.canvas).on('mousemove', (e) => this.onCanvasMouseMove(e));
			$(this.canvas).on('mouseup', (e) => this.onCanvasMouseUp(e));
			$(this.canvas).on('click', (e) => this.onCanvasClick(e));

			// Save annotations
			$('#save-annotations').on('click', () => this.saveAnnotations());

			// Share annotations
			$('#share-annotations').on('click', () => this.shareAnnotations());

			// Keyboard shortcuts
			$(document).on('keydown', (e) => this.handleKeyboard(e));
		},

		/**
		 * Update UI based on auth mode.
		 */
		updateAuthModeUI() {
			const isLoggedIn = !!DemocraticAnnotations.isLoggedIn;
			if (!isLoggedIn) {
				$('#share-annotations').prop('disabled', true).addClass('opacity-50 cursor-not-allowed');
				$('.annotation-auth-hint').removeClass('hidden');
			}
		},

		/**
     * Set annotation mode
     * @param mode
     */
		setAnnotationMode(mode) {
			this.annotationMode = mode;

			// Update UI
			$('.tool-btn').removeClass('active');
			$(`#tool-${mode}`).addClass('active');

			// Update cursor
			if (mode === 'none') {
				$(this.canvas).css('cursor', 'default');
			} else if (mode === 'highlight') {
				$(this.canvas).css('cursor', 'text');
			} else if (mode === 'note') {
				$(this.canvas).css('cursor', 'crosshair');
			} else if (mode === 'draw') {
				$(this.canvas).css('cursor', 'pen');
			}
		},

		/**
     * Handle canvas mouse down
     * @param e
     */
		onCanvasMouseDown(e) {
			if (this.annotationMode === 'draw') {
				this.isDrawing = true;
				const rect = this.canvas.getBoundingClientRect();
				const x = e.clientX - rect.left;
				const y = e.clientY - rect.top;
				this.drawingData = [[x, y]];
			}
		},

		/**
     * Handle canvas mouse move
     * @param e
     */
		onCanvasMouseMove(e) {
			if (this.isDrawing && this.annotationMode === 'draw') {
				const rect = this.canvas.getBoundingClientRect();
				const x = e.clientX - rect.left;
				const y = e.clientY - rect.top;
				this.drawingData.push([x, y]);

				// Draw line segment
				this.ctx.strokeStyle = '#FF0000';
				this.ctx.lineWidth = 2;
				this.ctx.lineCap = 'round';
				this.ctx.beginPath();
				const prev = this.drawingData[this.drawingData.length - 2];
				this.ctx.moveTo(prev[0], prev[1]);
				this.ctx.lineTo(x, y);
				this.ctx.stroke();
			}
		},

		/**
     * Handle canvas mouse up
     * @param e
     */
		onCanvasMouseUp(e) {
			if (this.isDrawing && this.annotationMode === 'draw') {
				this.isDrawing = false;

				// Save drawing annotation
				const annotation = {
					id: this.generateId(),
					type: 'draw',
					page: this.pageNum,
					data: this.drawingData,
					color: '#FF0000',
					width: 2,
					timestamp: new Date().toISOString(),
				};

				this.annotations.push(annotation);
				this.drawingData = [];
			}
		},

		/**
     * Handle canvas click
     * @param e
     */
		onCanvasClick(e) {
			const rect = this.canvas.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;

			if (this.annotationMode === 'note') {
				this.addNoteAnnotation(x, y);
			} else if (this.annotationMode === 'highlight') {
				this.addHighlightAnnotation(x, y);
			}
		},

		/**
     * Add note annotation
     * @param x
     * @param y
     */
		addNoteAnnotation(x, y) {
			const note = prompt('Enter your note:');
			if (!note) return;

			const annotation = {
				id: this.generateId(),
				type: 'note',
				page: this.pageNum,
				x,
				y,
				content: note,
				color: '#FFEB3B',
				timestamp: new Date().toISOString(),
			};

			this.annotations.push(annotation);
			this.renderAnnotations(this.pageNum);
		},

		/**
     * Add highlight annotation
     * @param x
     * @param y
     */
		addHighlightAnnotation(x, y) {
			// For simplicity, create a rectangular highlight
			// In production, implement text selection detection
			const annotation = {
				id: this.generateId(),
				type: 'highlight',
				page: this.pageNum,
				x,
				y,
				width: 200,
				height: 20,
				color: 'rgba(255, 235, 59, 0.4)',
				timestamp: new Date().toISOString(),
			};

			this.annotations.push(annotation);
			this.renderAnnotations(this.pageNum);
		},

		/**
     * Render annotations for current page
     * @param pageNum
     */
		renderAnnotations(pageNum) {
			const pageAnnotations = this.annotations.filter(
				(a) => a.page === pageNum,
			);

			pageAnnotations.forEach((annotation) => {
				if (annotation.type === 'note') {
					this.renderNoteAnnotation(annotation);
				} else if (annotation.type === 'highlight') {
					this.renderHighlightAnnotation(annotation);
				} else if (annotation.type === 'draw') {
					this.renderDrawingAnnotation(annotation);
				}
			});
		},

		/**
     * Render note annotation
     * @param annotation
     */
		renderNoteAnnotation(annotation) {
			// Draw note icon
			this.ctx.fillStyle = annotation.color;
			this.ctx.fillRect(annotation.x - 10, annotation.y - 10, 20, 20);

			// Add border
			this.ctx.strokeStyle = '#000';
			this.ctx.lineWidth = 1;
			this.ctx.strokeRect(annotation.x - 10, annotation.y - 10, 20, 20);

			// Add click handler to show note content
			// (In production, implement proper event handling)
		},

		/**
     * Render highlight annotation
     * @param annotation
     */
		renderHighlightAnnotation(annotation) {
			this.ctx.fillStyle = annotation.color;
			this.ctx.fillRect(
				annotation.x,
				annotation.y,
				annotation.width,
				annotation.height,
			);
		},

		/**
     * Render drawing annotation
     * @param annotation
     */
		renderDrawingAnnotation(annotation) {
			this.ctx.strokeStyle = annotation.color;
			this.ctx.lineWidth = annotation.width;
			this.ctx.lineCap = 'round';
			this.ctx.lineJoin = 'round';
			this.ctx.beginPath();

			if (annotation.data.length > 0) {
				this.ctx.moveTo(annotation.data[0][0], annotation.data[0][1]);
				for (let i = 1; i < annotation.data.length; i++) {
					this.ctx.lineTo(annotation.data[i][0], annotation.data[i][1]);
				}
				this.ctx.stroke();
			}
		},

		/**
     * Load annotations from server
     */
		async loadAnnotations() {
			const agendaId = $('#pdf-canvas').data('agenda-id');
			if (!agendaId) return;

			if (!DemocraticAnnotations.isLoggedIn) {
				this.loadLocalAnnotations();
				return;
			}

			try {
				const response = await fetch(
					`${DemocraticAnnotations.restUrl}/annotations?agenda_id=${agendaId}`,
					{
						headers: {
							'X-WP-Nonce': DemocraticAnnotations.nonce,
						},
					},
				);

				if (response.ok) {
					const payload = await response.json();
					const data = payload && payload.data ? payload.data : payload;
					this.annotations = data.annotations || [];
					this.syncLocalAnnotations();
					this.renderAnnotations(this.pageNum);
				}
			} catch (error) {
				console.error('Error loading annotations:', error);
				this.loadLocalAnnotations();
			}
		},

		/**
     * Save annotations to server
     */
		async saveAnnotations() {
			const agendaId = $('#pdf-canvas').data('agenda-id');
			if (!agendaId) return;

			this.saveLocalAnnotations();
			if (!DemocraticAnnotations.isLoggedIn) {
				this.showSuccess('Annotations saved on this device.');
				return;
			}

			try {
				const response = await fetch(
					`${DemocraticAnnotations.restUrl}/annotations`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-WP-Nonce': DemocraticAnnotations.nonce,
						},
						body: JSON.stringify({
							agenda_id: agendaId,
							annotations: this.annotations,
						}),
					},
				);

				if (response.ok) {
					this.showSuccess('Annotations saved successfully.');
				} else {
					throw new Error('Save failed');
				}
			} catch (error) {
				console.error('Error saving annotations:', error);
				this.showError('Failed to save annotations.');
			}
		},

		/**
     * Share annotations with officers
     */
		async shareAnnotations() {
			const agendaId = $('#pdf-canvas').data('agenda-id');
			if (!agendaId) return;

			if (!DemocraticAnnotations.isLoggedIn) {
				this.showError('Sign in to share annotations with officers.');
				return;
			}

			if (
				!confirm('Share your annotations with democratic services officers?')
			) {
				return;
			}

			try {
				const response = await fetch(
					`${DemocraticAnnotations.restUrl}/annotations/share`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'X-WP-Nonce': DemocraticAnnotations.nonce,
						},
						body: JSON.stringify({
							agenda_id: agendaId,
						}),
					},
				);

				if (response.ok) {
					this.showSuccess('Annotations shared with officers successfully.');
				} else {
					throw new Error('Share failed');
				}
			} catch (error) {
				console.error('Error sharing annotations:', error);
				this.showError('Failed to share annotations.');
			}
		},

		/**
     * Handle keyboard shortcuts
     * @param e
     */
		handleKeyboard(e) {
			// Arrow keys for navigation
			if (e.key === 'ArrowLeft') {
				this.onPrevPage();
				e.preventDefault();
			} else if (e.key === 'ArrowRight') {
				this.onNextPage();
				e.preventDefault();
			} else if (e.key === '+' || e.key === '=') {
				// Zoom shortcuts
				this.zoomIn();
				e.preventDefault();
			} else if (e.key === '-') {
				this.zoomOut();
				e.preventDefault();
			} else if (e.ctrlKey && e.key === 's') {
				// Save shortcut
				this.saveAnnotations();
				e.preventDefault();
			}
		},

		/**
     * Generate unique ID
     */
		generateId() {
			return `annotation_${Date.now()}_${Math.random()
				.toString(36)
				.substr(2, 9)}`;
		},

		/**
     * Show success message
     * @param message
     */
		showSuccess(message) {
			$('#annotation-message')
				.removeClass('error')
				.addClass('success')
				.text(message)
				.fadeIn()
				.delay(3000)
				.fadeOut();
		},

		/**
     * Show error message
     * @param message
     */
		showError(message) {
			$('#annotation-message')
				.removeClass('success')
				.addClass('error')
				.text(message)
				.fadeIn()
				.delay(5000)
				.fadeOut();
		},

		/**
		 * Load local annotations from browser storage.
		 */
		loadLocalAnnotations() {
			if (!this.localStorageKey || !window.localStorage) {
				this.annotations = [];
				return;
			}

			try {
				const raw = window.localStorage.getItem(this.localStorageKey);
				const parsed = raw ? JSON.parse(raw) : [];
				this.annotations = Array.isArray(parsed) ? parsed : [];
				this.renderAnnotations(this.pageNum);
			} catch (error) {
				console.error('Error loading local annotations:', error);
				this.annotations = [];
			}
		},

		/**
		 * Persist local annotations to browser storage.
		 */
		saveLocalAnnotations() {
			if (!this.localStorageKey || !window.localStorage) {
				return;
			}

			try {
				window.localStorage.setItem(
					this.localStorageKey,
					JSON.stringify(this.annotations),
				);
			} catch (error) {
				console.error('Error saving local annotations:', error);
			}
		},

		/**
		 * Keep local annotations aligned with latest server state for logged-in users.
		 */
		syncLocalAnnotations() {
			if (!this.localStorageKey || !window.localStorage) {
				return;
			}

			try {
				window.localStorage.setItem(
					this.localStorageKey,
					JSON.stringify(this.annotations),
				);
			} catch (error) {
				console.error('Error syncing local annotations:', error);
			}
		},
	};

	// Initialize on document ready
	$(document).ready(function () {
		AgendaAnnotations.init();
	});
})(jQuery);
